import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle, X, ChevronRight, ChevronLeft, Sparkles,
  Globe, Minimize2, Maximize2, RotateCcw, ExternalLink,
  LayoutDashboard, TrendingUp, Users, Wallet, BarChart3,
  FileText, Settings, ArrowDownToLine, ArrowUpToLine,
  ShieldCheck, Shield, Puzzle, UsersRound, Code2, Bell,
  Brain, Layers, ClipboardList, BookOpen, Home,
} from 'lucide-react';

// ─── Language definitions ─────────────────────────────────────────────────────
const LANGUAGES = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'ar', label: 'العربية',   flag: '🇸🇦', rtl: true },
  { code: 'fr', label: 'Français',  flag: '🇫🇷' },
  { code: 'es', label: 'Español',   flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch',   flag: '🇩🇪' },
  { code: 'zh', label: '中文',       flag: '🇨🇳' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'ru', label: 'Русский',   flag: '🇷🇺' },
];

// ─── Route map (matches real sidebar routes) ──────────────────────────────────
const ROUTES = {
  dashboard:          { path: '/',                  icon: LayoutDashboard,  label: 'Dashboard' },
  clients:            { path: '/clients',           icon: Users,            label: 'Clients' },
  accountGroups:      { path: '/account-groups',    icon: UsersRound,       label: 'Account Groups' },
  instruments:        { path: '/instruments',       icon: TrendingUp,       label: 'Instruments' },
  cascade:            { path: '/cascade-settings',  icon: Layers,           label: 'Cascade Guide' },
  accounts:           { path: '/accounts',          icon: Wallet,           label: 'Accounts' },
  trading:            { path: '/trading',           icon: BarChart3,        label: 'Trading' },
  reports:            { path: '/reports',           icon: FileText,         label: 'Reports' },
  analytics:          { path: '/analytics',         icon: Brain,            label: 'Advanced Analytics' },
  deposits:           { path: '/deposits',          icon: ArrowDownToLine,  label: 'Deposits' },
  withdrawals:        { path: '/withdrawals',       icon: ArrowUpToLine,    label: 'Withdrawals' },
  plugins:            { path: '/plugins',           icon: Puzzle,           label: 'Plugin Store' },
  kyc:                { path: '/broker/kyc',        icon: ClipboardList,    label: 'KYC Dashboard' },
  kycConfig:          { path: '/plugins/kyc_verification', icon: Shield,   label: 'KYC Config' },
  auditLog:           { path: '/audit-log',         icon: ShieldCheck,      label: 'Audit Log' },
  notifications:      { path: '/notifications',     icon: Bell,             label: 'Notifications' },
  apiDocs:            { path: '/api-docs',          icon: Code2,            label: 'REST API' },
  settings:           { path: '/settings',          icon: Settings,         label: 'Settings' },
};

// ─── Full Knowledge Base ──────────────────────────────────────────────────────
const KB = {
  en: {
    greeting: "👋 Hi! I'm your Dashboard Assistant. Pick a topic below to get started.",
    categories_label: "What do you need help with?",
    back_topics: "All Topics",
    back_answer: "Back",
    go_to_page: "Go to page",
    also_see: "Also relevant:",
    topics: [
      {
        key: 'dashboard',
        label: 'Dashboard Overview',
        icon: 'LayoutDashboard',
        color: 'var(--accent-primary)',
        description: 'Stats, usage limits & plan info',
        questions: [
          {
            q: "What does the Dashboard show?",
            a: "The **Dashboard** is your command center. It displays:\n• **Total Clients** — registered client count\n• **Total Deposits** — cumulative funds deposited\n• **Open Positions** — live trades across all accounts\n• **Revenue** — broker earnings from spreads and commissions\n\nIt also shows your **subscription plan usage rings** — circular gauges showing how many accounts, instruments, and clients you've used versus your plan limit. Color changes from green → yellow → red as you approach limits.",
            routes: ['dashboard'],
          },
          {
            q: "What are the usage rings on the Dashboard?",
            a: "Usage rings are circular progress indicators that show your plan consumption:\n• **Accounts ring** — trading accounts created vs. your plan max\n• **Clients ring** — registered clients vs. your plan max\n• **Instruments ring** — active symbols vs. your plan max\n\nWhen a ring turns **yellow**, you're at 70%+ capacity. When it turns **red**, you're at 90%+ and should consider upgrading your plan.",
            routes: ['dashboard'],
          },
          {
            q: "What are Allowed Markets on the Dashboard?",
            a: "**Allowed Markets** badges show which asset classes your current plan permits:\n• ✅ Green badge = trading enabled for that market\n• ❌ Grey badge = not included in your plan\n\nMarkets include Forex, Crypto, Indices, Commodities, Stocks, and ETFs. To unlock additional markets, upgrade your subscription plan.",
            routes: ['dashboard'],
          },
          {
            q: "How do I see recent pending actions on the Dashboard?",
            a: "The Dashboard shows quick-access cards for:\n• **Pending Deposits** — awaiting your approval\n• **Pending Withdrawals** — awaiting your approval\n• **Pending KYC** — clients awaiting identity verification\n\nClick any card to jump directly to that section and take action.",
            routes: ['dashboard', 'deposits', 'withdrawals', 'kyc'],
          },
        ],
      },

      {
        key: 'instruments',
        label: 'Instruments',
        icon: 'TrendingUp',
        color: '#3b82f6',
        description: 'Symbols, spreads, lot sizes & pricing',
        questions: [
          {
            q: "How do I configure an instrument's spread?",
            a: "1. Go to **Instruments** in the sidebar\n2. Find your symbol using the search bar or category tabs (Forex, Crypto, Indices…)\n3. Click the ⚙️ **settings icon** on that row\n4. In the modal, set:\n   • **Spread** (in pips)\n   • **Min/Max lot sizes**\n   • **Commission** per lot\n   • **Swap rates** (long & short)\n5. Click **Save**\n\nNote: These are the broker-level defaults. You can override per group in **Account Groups → Pricing Overrides**.",
            routes: ['instruments', 'accountGroups'],
          },
          {
            q: "How do I add a new trading symbol?",
            a: "1. Go to **Instruments**\n2. Click **+ Add Symbol** (top-right button)\n3. Search the catalog for the symbol you want\n4. Select it and configure:\n   • Spread, min/max lot, commission\n   • Swap rates, contract size\n5. Click **Save**\n\nThe symbol will immediately appear in your instrument list. Clients can trade it once enabled.",
            routes: ['instruments'],
          },
          {
            q: "How do I enable or disable a symbol?",
            a: "On the **Instruments** page, each symbol row has a **toggle switch** on the right:\n• Toggle **ON** (green) → symbol is live and tradeable by clients\n• Toggle **OFF** (grey) → symbol is hidden from clients and cannot be traded\n\nThis is useful for maintenance windows, regulatory restrictions, or temporarily suspending a market.",
            routes: ['instruments'],
          },
          {
            q: "Where do live instrument prices come from?",
            a: "Prices update in **real-time** from your configured liquidity feed and are shown as **Bid / Ask** on the Instruments page. You cannot manually set prices.\n\nTo change your liquidity provider or price feed priority, go to **Cascade Guide** in the sidebar — this controls the order in which price feeds are consumed.",
            routes: ['instruments', 'cascade'],
          },
          {
            q: "What are instrument categories (Forex, Crypto, etc.)?",
            a: "Category tabs on the **Instruments** page filter the symbol list by asset class:\n• **Forex** — currency pairs\n• **Crypto** — digital assets\n• **Indices** — stock market indices\n• **Commodities** — oil, gold, silver…\n• **Stocks** — individual equities\n• **ETFs** — exchange-traded funds\n\nWhich categories are available depends on your subscription plan. Check **Dashboard → Allowed Markets** to see which are active.",
            routes: ['instruments', 'dashboard'],
          },
          {
            q: "How do I set per-group pricing overrides for instruments?",
            a: "Instrument settings are broker-level defaults. To override pricing **per account group**:\n1. Go to **Account Groups**\n2. Click a group\n3. Click **Pricing Overrides**\n4. Set group-specific spread markup, commission, and leverage\n\nLeave fields empty (NULL) to fall through to broker defaults. This is the **cascade system** — client → group → instrument → broker defaults.",
            routes: ['accountGroups', 'instruments', 'cascade'],
          },
        ],
      },

      {
        key: 'accounts',
        label: 'Accounts',
        icon: 'Wallet',
        color: '#10b981',
        description: 'Trading accounts, balances & leverage',
        questions: [
          {
            q: "How do I view and manage all trading accounts?",
            a: "Go to **Accounts** in the sidebar. You'll see a full list of all trading accounts with:\n• **Balance** — current cash balance\n• **Equity** — balance + floating P&L\n• **Margin** — margin currently in use\n• **Free Margin** — available margin\n• **Status** — active, disabled, read-only\n\nUse the search bar and filters to find specific accounts. Click any account row to open the full detail view.",
            routes: ['accounts'],
          },
          {
            q: "How do I manually adjust an account balance (credit/debit)?",
            a: "1. Open the account from **Accounts**\n2. Click the **Credit/Debit** button\n3. Enter the amount:\n   • Positive value = credit (add funds)\n   • Negative value = debit (remove funds)\n4. Select a reason/note from the dropdown\n5. Confirm\n\nThe adjustment is applied instantly and is fully recorded in the **Audit Log** with your admin name, timestamp, and reason.",
            routes: ['accounts', 'auditLog'],
          },
          {
            q: "How do I change an account's leverage?",
            a: "1. Open the account from **Accounts**\n2. Click **Edit Account**\n3. Change the **Max Leverage** field\n4. Save\n\nAlternatively, set a leverage cap at the **group level** via **Account Groups → Pricing Overrides** — this applies to all accounts in that group and overrides individual account settings if set lower.",
            routes: ['accounts', 'accountGroups'],
          },
          {
            q: "How do I disable or restrict a trading account?",
            a: "Open the account from **Accounts**, then use these options:\n• **Disable Account** — prevents login and all trading\n• **Read-Only** — allows login but no new trades\n• **Close Only** — allows closing existing positions only\n\nAll status changes are logged in the **Audit Log**.",
            routes: ['accounts', 'auditLog'],
          },
          {
            q: "How do I assign an account to a group?",
            a: "1. Open the account from **Accounts**\n2. Click **Edit Account**\n3. Change the **Account Group** dropdown to the desired group\n4. Save\n\nThe account will immediately inherit that group's pricing overrides (spread markup, commission, leverage cap). You can manage groups in **Account Groups**.",
            routes: ['accounts', 'accountGroups'],
          },
        ],
      },

      {
        key: 'groups',
        label: 'Account Groups',
        icon: 'UsersRound',
        color: '#f59e0b',
        description: 'Groups, pricing overrides & cascade',
        questions: [
          {
            q: "What are Account Groups and why use them?",
            a: "**Account Groups** let you apply shared pricing settings to a set of trading accounts. Instead of configuring each account individually, you define a group (e.g. 'VIP', 'Standard', 'Islamic') and all accounts in it inherit those settings.\n\nGroups control:\n• Spread markup above broker default\n• Commission per lot\n• Maximum leverage cap\n\nThis is the **middle layer** of the cascade: Client → **Group** → Instrument → Broker defaults.",
            routes: ['accountGroups', 'cascade'],
          },
          {
            q: "How do I create a new Account Group?",
            a: "1. Go to **Account Groups**\n2. Click **+ New Group**\n3. Enter a name and description\n4. Optionally set it as the **default group** (new accounts auto-join)\n5. Save\n\nAfter creating the group, click **Pricing Overrides** to configure spread, commission, and leverage for that group.",
            routes: ['accountGroups'],
          },
          {
            q: "How do I set pricing overrides for a group?",
            a: "1. Go to **Account Groups**\n2. Click the ⚙️ edit icon on a group\n3. Set overrides:\n   • **Spread Markup** (0–5 pips slider) — added on top of instrument spread\n   • **Commission Per Lot** (0–$20 slider) — charged per round-turn lot\n   • **Max Leverage** (dropdown: 1:1 to 1:2000)\n4. Leave any field **empty** to use the broker-level default for that setting\n5. Save\n\nChanges apply immediately to all accounts in the group.",
            routes: ['accountGroups'],
          },
          {
            q: "What is the 'Default' group?",
            a: "The **Default group** is automatically assigned to new trading accounts unless you specify otherwise during account creation.\n\nMark a group as default from **Account Groups** by toggling the 'Default' option when editing it. Only one group can be the default at a time.",
            routes: ['accountGroups'],
          },
          {
            q: "How does the cascade priority system work?",
            a: "The **Cascade** is a 3-layer inheritance system for pricing:\n\n**P1 — Client level** (highest priority)\nOverrides set directly on an individual client. Used for VIP or restricted accounts.\n\n**P2 — Group level**\nApplies to all accounts in a group. Overrides instrument defaults.\n\n**P3 — Instrument level**\nPer-symbol broker defaults. Falls back to platform defaults if empty.\n\nLeave a field **empty (NULL)** at any layer to inherit from the layer below. See the **Cascade Guide** for a visual walkthrough.",
            routes: ['cascade', 'accountGroups', 'clients', 'instruments'],
          },
        ],
      },

      {
        key: 'clients',
        label: 'Clients',
        icon: 'Users',
        color: '#8b5cf6',
        description: 'Client management, KYC & overrides',
        questions: [
          {
            q: "How do I find and view a specific client?",
            a: "Go to **Clients** in the sidebar. Use:\n• **Search bar** — search by name, email, or account ID\n• **Status filter** — filter by Active, Disabled, or KYC Pending\n• **Sort options** — sort by registration date, name, or balance\n\nClick any client card to open their full profile, view their accounts, KYC status, and available actions.",
            routes: ['clients'],
          },
          {
            q: "How do I disable or re-enable a client account?",
            a: "1. Open the client from **Clients**\n2. Click **Disable Client**\n3. Optionally add a reason note\n4. Confirm\n\nA disabled client cannot log in or trade. To re-enable, open the same client and click **Enable Client**. All actions are logged in the **Audit Log**.",
            routes: ['clients', 'auditLog'],
          },
          {
            q: "How do I set client-level pricing overrides?",
            a: "Client-level overrides are **P1 — the highest priority** in the cascade system. They override group and instrument defaults.\n\n1. Open the client from **Clients**\n2. Click **Override Settings** (or similar)\n3. Set spread markup, commission, or leverage cap specifically for this client\n4. Leave fields empty to fall back to their group settings\n\nUse this for VIP clients who get tighter spreads, or restricted clients with reduced leverage.",
            routes: ['clients', 'accountGroups', 'cascade'],
          },
          {
            q: "How do I update a client's KYC status?",
            a: "There are two ways:\n\n**From Clients page:**\nOpen the client → click **Update KYC** → select the new status (Pending, Approved, Rejected)\n\n**From KYC Dashboard** (requires KYC plugin):\nGo to **KYC Dashboard** → find the client → review their submitted documents → click **Approve** or **Reject** with a note\n\nKYC status affects whether clients can deposit, withdraw, or trade depending on your platform settings.",
            routes: ['clients', 'kyc'],
          },
          {
            q: "Can I edit a client's personal details?",
            a: "Yes. Open the client from **Clients** and click the **Edit** (pencil) icon. You can update:\n• Display name\n• Email address\n• Phone number\n• Country\n• Account group assignment\n\nAll edits are saved immediately and logged in the **Audit Log**.",
            routes: ['clients', 'auditLog'],
          },
        ],
      },

      {
        key: 'kyc',
        label: 'KYC Verification',
        icon: 'Shield',
        color: '#ef4444',
        description: 'Identity verification & document review',
        questions: [
          {
            q: "Where do I review and approve KYC applications?",
            a: "Go to **KYC Dashboard** (under Plugins in the sidebar — requires KYC plugin to be active).\n\nYou'll see all clients grouped by status:\n• **Pending** — submitted, awaiting review\n• **Approved** — identity verified\n• **Rejected** — failed verification\n\nClick any client to open the **Review Modal** where you can view uploaded documents, zoom in, and approve or reject with a written note.",
            routes: ['kyc', 'kycConfig'],
          },
          {
            q: "How do I approve or reject a KYC application?",
            a: "1. Go to **KYC Dashboard**\n2. Find the pending application\n3. Click to open the review panel\n4. Review the uploaded documents (passport, utility bill, selfie, etc.)\n5. Click **Approve** to verify the client, or **Reject** and enter a rejection reason\n\nThe client is notified automatically (if email notifications are configured). The decision is recorded in the **Audit Log** with your admin name and timestamp.",
            routes: ['kyc', 'auditLog', 'notifications'],
          },
          {
            q: "How do I configure KYC verification levels?",
            a: "Go to **KYC Config** (via Plugins → KYC Verification → KYC Config in the sidebar).\n\nHere you can set:\n• Which **documents** are required (ID, proof of address, selfie)\n• **Verification levels** (Level 1 = basic, Level 2 = full, etc.)\n• Trading **restrictions** per level (deposit limits, withdrawal limits)\n• Auto-approve rules\n\nThis page is only available when the KYC Verification plugin is active.",
            routes: ['kycConfig', 'kyc', 'plugins'],
          },
          {
            q: "What KYC levels are available?",
            a: "KYC levels represent how thoroughly a client has been verified:\n• **Level 0** — Unverified (no documents submitted)\n• **Level 1** — Basic (email verified, basic info)\n• **Level 2** — Standard (ID document verified)\n• **Level 3** — Full (ID + proof of address + selfie)\n\nHigher levels unlock higher deposit/withdrawal limits and trading permissions. Configure level requirements in **KYC Config**.",
            routes: ['kyc', 'kycConfig'],
          },
        ],
      },

      {
        key: 'deposits',
        label: 'Deposits',
        icon: 'ArrowDownToLine',
        color: '#10b981',
        description: 'Incoming funds & manual deposits',
        questions: [
          {
            q: "How do I view all deposit requests?",
            a: "Go to **Deposits** in the sidebar. You'll see:\n• **Pending** — awaiting review/approval\n• **Approved** — funds credited to account\n• **Rejected** — deposit declined\n\nUse the **date range picker** and **status filter** to narrow results. Each row shows the client, amount, currency, payment method, and submission time. Click any row to expand full details.",
            routes: ['deposits'],
          },
          {
            q: "How do I approve or reject a deposit?",
            a: "1. Go to **Deposits**\n2. Find the pending deposit (shown at top)\n3. Click the row to expand it\n4. Review the details and any proof of payment document\n5. Click **Approve** to credit the funds, or **Reject** and add a note explaining why\n\nApproved deposits are credited to the client's trading account immediately. All actions are logged in the **Audit Log**.",
            routes: ['deposits', 'auditLog'],
          },
          {
            q: "How do I add a manual deposit for a client?",
            a: "1. Go to **Deposits**\n2. Click **+ Manual Deposit**\n3. Select the **client account**\n4. Enter the **amount** and **currency**\n5. Add a **reference note** (bank transfer ref, reason, etc.)\n6. Confirm\n\nThe funds are credited immediately to the account. This is used for wire transfers, crypto payments handled outside the gateway, or promotional credits.",
            routes: ['deposits'],
          },
          {
            q: "How do I view proof of payment for a deposit?",
            a: "Expand any deposit row on the **Deposits** page. If the client uploaded a proof document (bank receipt, screenshot), a **View Proof** button will appear.\n\nClick it to open the document viewer — you can zoom in to verify it before approving.",
            routes: ['deposits'],
          },
        ],
      },

      {
        key: 'withdrawals',
        label: 'Withdrawals',
        icon: 'ArrowUpToLine',
        color: '#f59e0b',
        description: 'Withdrawal requests & approvals',
        questions: [
          {
            q: "How do I approve or reject a withdrawal request?",
            a: "1. Go to **Withdrawals** in the sidebar\n2. Pending requests appear at the top\n3. Click a request to expand it and review:\n   • Client name and account\n   • Amount and currency\n   • Payout destination (bank, crypto wallet, e-wallet)\n4. Click **Approve** or **Reject**\n5. If rejecting, add a note explaining the reason\n\nApproved withdrawals are processed through your configured payment gateway. All actions are logged in the **Audit Log**.",
            routes: ['withdrawals', 'auditLog'],
          },
          {
            q: "How do I view withdrawal payout details?",
            a: "Expand any withdrawal row on the **Withdrawals** page. You'll see the full payout details:\n• Bank account number / IBAN\n• Crypto wallet address\n• E-wallet ID\n\nThere's a **copy button** next to each detail for quick copying to your payment processing system.",
            routes: ['withdrawals'],
          },
          {
            q: "Can I add a note when rejecting a withdrawal?",
            a: "Yes. When you click **Reject** on a withdrawal request, a text field appears for you to enter a rejection reason. This note:\n• Is recorded in the **Audit Log**\n• May be sent to the client as a notification (if configured in **Notification Settings**)\n\nAlways add a clear reason so clients understand why and can re-submit correctly.",
            routes: ['withdrawals', 'auditLog', 'notifications'],
          },
        ],
      },

      {
        key: 'trading',
        label: 'Live Trading',
        icon: 'BarChart3',
        color: '#3b82f6',
        description: 'Monitor open positions & trade history',
        questions: [
          {
            q: "What does the Trading page show?",
            a: "The **Trading** page is the live trade monitor. It shows:\n• **Open Positions** — all currently active trades across all clients\n• **Pending Orders** — limit/stop orders not yet triggered\n• **Trade History** — closed trades with P&L\n\nTrades are grouped by **client/account** so you can drill into a specific trader's activity. Each trade shows symbol, direction, size, open price, current price, P&L, and margin used.",
            routes: ['trading'],
          },
          {
            q: "How do I close a trade on behalf of a client?",
            a: "1. Go to **Trading**\n2. Find the client's account and expand their position list\n3. Find the trade you want to close\n4. Click the **Close** button on that row\n5. Confirm the action\n\nThe trade is closed at current market price. This is recorded in the **Audit Log** with your admin name. Use this for emergency risk management or client requests.",
            routes: ['trading', 'auditLog'],
          },
          {
            q: "How do I modify a trade's stop loss or take profit?",
            a: "1. Go to **Trading**\n2. Find the open position\n3. Click the **Modify** button\n4. Update the **Stop Loss** and/or **Take Profit** levels\n5. Save\n\nModifications take effect immediately on the trading platform.",
            routes: ['trading'],
          },
          {
            q: "How do I cancel a pending order?",
            a: "1. Go to **Trading**\n2. Switch to the **Pending Orders** tab\n3. Find the order to cancel\n4. Click **Cancel Order**\n5. Confirm\n\nThe order is removed and the client's margin is freed.",
            routes: ['trading'],
          },
          {
            q: "What is the Margin Level shown on Trading?",
            a: "**Margin Level** = (Equity / Used Margin) × 100%\n\nIt indicates how close an account is to a margin call:\n• **> 100%** — Safe\n• **< 100%** — Margin call risk\n• **< Stop Out level** — Positions may be auto-closed\n\nAccounts with low margin levels are highlighted in the Trading view. You can see per-account margin levels in the trader panel.",
            routes: ['trading', 'accounts'],
          },
        ],
      },

      {
        key: 'reports',
        label: 'Reports & Analytics',
        icon: 'FileText',
        color: '#ec4899',
        description: 'P&L reports, exports & advanced analytics',
        questions: [
          {
            q: "What reports are available?",
            a: "The **Reports** page provides:\n• **P&L Summary** — broker revenue from spreads and commissions\n• **Cash Flow** — deposits vs withdrawals over time\n• **Trade History** — all closed trades with P&L breakdown\n• **Client Performance** — top/bottom performing clients\n• **Instrument Popularity** — most traded symbols\n\nAll reports support date range filtering and can be **exported as CSV or PDF**.",
            routes: ['reports'],
          },
          {
            q: "How do I export a report?",
            a: "1. Go to **Reports**\n2. Select the report type\n3. Set the **date range** using the date picker\n4. Apply any additional filters (client, instrument, account group)\n5. Click **Export CSV** or **Export PDF**\n\nThe file downloads immediately to your browser.",
            routes: ['reports'],
          },
          {
            q: "What is Advanced Analytics?",
            a: "**Advanced Analytics** (requires the Advanced Analytics plugin) provides deeper business intelligence:\n• **CLV Segments** — client lifetime value analysis\n• **Risk Heatmap** — exposure concentration by instrument\n• **Symbol Exposure** — net broker exposure per symbol\n• **Revenue Trends** — daily/weekly/monthly revenue breakdown\n• **Margin Utilization** — across all accounts over time\n\nCharts are interactive — hover to see data points, click legend to toggle series.",
            routes: ['analytics', 'plugins'],
          },
          {
            q: "Where is the Audit Log?",
            a: "Go to **Audit Log** in the sidebar (under System section).\n\nEvery admin action is recorded here:\n• Balance adjustments (credit/debit)\n• Account status changes\n• KYC decisions (approve/reject)\n• Trade modifications and closures\n• Settings changes\n\nEach entry shows: timestamp, admin user, action type, affected entity, and before/after values. Use the **search bar** and **date filter** to find specific events.",
            routes: ['auditLog'],
          },
        ],
      },

      {
        key: 'settings',
        label: 'Settings',
        icon: 'Settings',
        color: '#6366f1',
        description: 'Broker profile, localization & config',
        questions: [
          {
            q: "How do I update broker name, logo, and contact info?",
            a: "1. Go to **Settings**\n2. Open the **Broker Profile** section\n3. Update:\n   • **Broker Name** — shown in client-facing interfaces\n   • **Logo** — upload PNG/SVG, recommended 200×200px\n   • **Support Email** — clients contact this address\n   • **Website / Domain** — your branded domain\n4. Click **Save Changes**\n\nChanges take effect immediately across the platform.",
            routes: ['settings'],
          },
          {
            q: "How do I enable multiple languages for clients?",
            a: "1. Go to **Settings → Localization**\n2. Toggle on the languages you want to offer clients\n3. Save\n\nEnabled languages appear in the client-facing language selector. The admin dashboard language is controlled separately via the locale picker in the top navigation bar.",
            routes: ['settings'],
          },
          {
            q: "What is the Cascade Settings page?",
            a: "The **Cascade Guide** page explains and visualizes the 3-layer pricing inheritance system:\n\n**Layer 1 (P1)** — Client overrides (highest priority)\n**Layer 2 (P2)** — Group overrides\n**Layer 3 (P3)** — Instrument defaults\n\nIt shows which layer each setting is currently sourced from for any given account. Use it to debug unexpected pricing or to understand why a client is seeing a certain spread.",
            routes: ['cascade', 'settings', 'accountGroups'],
          },
          {
            q: "How do I configure notification settings?",
            a: "Go to **Notifications** in the sidebar.\n\nYou can toggle alerts for:\n• **KYC events** — new applications, approvals, rejections\n• **Deposit requests** — new pending deposits\n• **Withdrawal requests** — new pending withdrawals\n• **Margin calls** — accounts approaching stop-out\n• **System events** — errors, plugin status\n\nFor each alert, set the **email recipients** and optionally configure **SMS** or **push notifications** (FCM) if those channels are enabled.",
            routes: ['notifications'],
          },
          {
            q: "How do I access and manage REST API keys?",
            a: "Go to **REST API** in the sidebar (visible only if you have API access on your plan).\n\nFrom there:\n• View the full **API reference** — all endpoints, parameters, response examples\n• Click **Generate API Key** in the Authentication section\n• Copy and store the key securely — it grants full broker API access\n• **Revoke** any key at any time from the same panel\n\nUse the API to integrate your broker platform with external CRMs, reporting tools, or custom client portals.",
            routes: ['apiDocs'],
          },
        ],
      },

      {
        key: 'plugins',
        label: 'Plugins & Extensions',
        icon: 'Puzzle',
        color: '#f97316',
        description: 'Install, manage & configure plugins',
        questions: [
          {
            q: "What plugins are available?",
            a: "Go to **Plugin Store** to browse all available plugins. Current plugins include:\n• **KYC Verification** — document review, identity verification levels\n• **Advanced Analytics** — deep business intelligence dashboards\n• **Pages** — custom landing/information pages for clients\n• **Credits** — credit/bonus system for client accounts\n• **Email Templates** — customizable transactional email templates\n\nSome plugins are included in your plan; others are paid add-ons. Check the pricing badge on each plugin card.",
            routes: ['plugins'],
          },
          {
            q: "How do I install or activate a plugin?",
            a: "1. Go to **Plugin Store**\n2. Browse or search for the plugin\n3. Click the plugin card to open its detail page\n4. Click **Install** (free) or **Purchase** (paid)\n5. After installation, toggle the **Active** switch to enable it\n\nOnce active, the plugin appears in the sidebar navigation. Some plugins require additional configuration — follow the in-page setup guide on the plugin detail page.",
            routes: ['plugins'],
          },
          {
            q: "How do I configure a plugin after installing it?",
            a: "After installing a plugin, it appears in the sidebar. For example:\n• KYC Verification → shows **KYC Dashboard** and **KYC Config** links\n• Advanced Analytics → shows **Advanced Analytics** link\n\nFor plugin-specific settings, go to **Plugin Store** → click the plugin → click **Configure** or navigate to the plugin's dedicated settings page via the sidebar.",
            routes: ['plugins', 'kyc', 'kycConfig', 'analytics'],
          },
          {
            q: "How do I deactivate a plugin?",
            a: "1. Go to **Plugin Store**\n2. Find the active plugin\n3. Toggle the **Active** switch OFF\n\nThe plugin is deactivated and its sidebar links disappear, but your configuration is preserved. You can reactivate it at any time without losing settings.",
            routes: ['plugins'],
          },
        ],
      },
    ],
  },
};

// Add minimal translations for other languages (greeting + topic labels only, full EN content as fallback)
['ar','fr','es','de','zh','pt','ru'].forEach(code => {
  const greetings = {
    ar: "👋 مرحباً! أنا مساعد لوحة التحكم. اختر موضوعاً أدناه للبدء.",
    fr: "👋 Bonjour ! Je suis votre assistant. Choisissez un sujet ci-dessous.",
    es: "👋 ¡Hola! Soy tu asistente. Elige un tema para comenzar.",
    de: "👋 Hallo! Ich bin Ihr Dashboard-Assistent. Wählen Sie ein Thema.",
    zh: "👋 你好！我是仪表板助手。请选择下方主题开始。",
    pt: "👋 Olá! Sou o assistente. Escolha um tópico abaixo para começar.",
    ru: "👋 Привет! Я ваш помощник. Выберите тему ниже.",
  };
  KB[code] = { ...KB.en, greeting: greetings[code] };
});

// ─── Icon map ─────────────────────────────────────────────────────────────────
const ICON_MAP = {
  LayoutDashboard, TrendingUp, Users, Wallet, BarChart3, FileText,
  Settings, ArrowDownToLine, ArrowUpToLine, Shield, Puzzle, UsersRound,
  Brain, Layers, ClipboardList, ShieldCheck, Bell, Code2,
};

// ─── Render bold/newline markdown ─────────────────────────────────────────────
function renderText(text) {
  return text.split('\n').map((line, li) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    const rendered = parts.map((p, i) =>
      i % 2 === 1
        ? <strong key={i} style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{p}</strong>
        : p
    );
    return <span key={li}>{rendered}{li < text.split('\n').length - 1 && <br />}</span>;
  });
}

// ─── Route Link Button ────────────────────────────────────────────────────────
function RouteLink({ routeKey, navigate, onClose }) {
  const route = ROUTES[routeKey];
  if (!route) return null;
  const Icon = route.icon;
  return (
    <button
      onClick={() => { navigate(route.path); onClose(); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 20,
        border: '1px solid var(--accent-primary)',
        backgroundColor: 'var(--accent-primary)10',
        color: 'var(--accent-primary)', fontSize: 11, fontWeight: 700,
        cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--accent-primary)'; e.currentTarget.style.color = '#fff'; }}
      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'var(--accent-primary)10'; e.currentTarget.style.color = 'var(--accent-primary)'; }}
      title={`Go to ${route.label}`}
    >
      <Icon size={10} />
      {route.label}
      <ExternalLink size={9} />
    </button>
  );
}

// ─── Answer View ──────────────────────────────────────────────────────────────
function AnswerView({ question, topic, onBack, navigate, onClose, isRTL }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 14px' }}
      className="helpbot-scroll">
      {/* Back */}
      <button onClick={onBack} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--accent-primary)', fontSize: 12, fontWeight: 700,
        padding: '14px 0 10px', width: '100%',
        textAlign: isRTL ? 'right' : 'left',
      }}>
        <ChevronLeft size={14} /> Back to {topic.label}
      </button>

      {/* Question */}
      <div style={{
        padding: '12px 14px', borderRadius: 12, marginBottom: 12,
        backgroundColor: topic.color + '15',
        border: `1px solid ${topic.color}30`,
      }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {question.q}
        </p>
      </div>

      {/* Answer */}
      <div style={{
        padding: '14px', borderRadius: 12, marginBottom: 14,
        backgroundColor: 'var(--bg-tertiary)',
        border: '1px solid var(--border-color)',
        fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7,
      }}>
        {renderText(question.a)}
      </div>

      {/* Route Links */}
      {question.routes?.length > 0 && (
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Go directly to:
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {question.routes.map(rk => (
              <RouteLink key={rk} routeKey={rk} navigate={navigate} onClose={onClose} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Topic View ───────────────────────────────────────────────────────────────
function TopicView({ topic, onBack, onSelectQuestion, isRTL }) {
  const Icon = ICON_MAP[topic.icon] || HelpCircle;
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 14px 14px' }}
      className="helpbot-scroll">
      {/* Back */}
      <button onClick={onBack} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--accent-primary)', fontSize: 12, fontWeight: 700,
        padding: '14px 0 10px', width: '100%',
        textAlign: isRTL ? 'right' : 'left',
      }}>
        <ChevronLeft size={14} /> All Topics
      </button>

      {/* Topic Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        padding: '10px 14px', borderRadius: 12,
        backgroundColor: topic.color + '12',
        border: `1px solid ${topic.color}25`,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
          backgroundColor: topic.color + '20',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} style={{ color: topic.color }} />
        </div>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{topic.label}</p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-tertiary)' }}>{topic.description}</p>
        </div>
      </div>

      {/* Questions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {topic.questions.map((qa, i) => (
          <button key={i} onClick={() => onSelectQuestion(qa)}
            style={{
              width: '100%', textAlign: isRTL ? 'right' : 'left',
              padding: '10px 12px', borderRadius: 10,
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-tertiary)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = topic.color; e.currentTarget.style.backgroundColor = topic.color + '08'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
          >
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, lineHeight: 1.45, flex: 1 }}>
              {qa.q}
            </span>
            <ChevronRight size={13} style={{ color: topic.color, flexShrink: 0 }} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Home View ────────────────────────────────────────────────────────────────
function HomeView({ topics, greeting, onSelectTopic, isRTL }) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 14 }} className="helpbot-scroll">
      {/* Greeting */}
      <div style={{
        padding: '12px 14px', borderRadius: 12, marginBottom: 16,
        backgroundColor: 'var(--bg-tertiary)',
        border: '1px solid var(--border-color)',
        fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6,
      }}>
        {greeting}
      </div>

      {/* Topic Grid */}
      <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
        Select a topic
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {topics.map((topic) => {
          const Icon = ICON_MAP[topic.icon] || HelpCircle;
          return (
            <button key={topic.key} onClick={() => onSelectTopic(topic)}
              style={{
                padding: '12px 10px', borderRadius: 12,
                border: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-tertiary)',
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
                textAlign: isRTL ? 'right' : 'left',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = topic.color; e.currentTarget.style.backgroundColor = topic.color + '0a'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 9,
                backgroundColor: topic.color + '20',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={15} style={{ color: topic.color }} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{topic.label}</p>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2, lineHeight: 1.3 }}>{topic.description}</p>
              </div>
              <div style={{ alignSelf: 'flex-end', fontSize: 9, color: 'var(--text-tertiary)', opacity: 0.7 }}>
                {topic.questions.length} questions
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main HelpBot ─────────────────────────────────────────────────────────────
export default function HelpBot() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [lang, setLang] = useState('en');
  const [showLangPicker, setShowLangPicker] = useState(false);
  const [view, setView] = useState('home'); // 'home' | 'topic' | 'answer'
  const [activeTopic, setActiveTopic] = useState(null);
  const [activeQuestion, setActiveQuestion] = useState(null);

  const t = KB[lang] || KB.en;
  const isRTL = LANGUAGES.find(l => l.code === lang)?.rtl || false;
  const currentLang = LANGUAGES.find(l => l.code === lang);

  const handleClose = () => { setOpen(false); setMinimized(false); };
  const handleReset = () => { setView('home'); setActiveTopic(null); setActiveQuestion(null); };

  const handleSelectTopic = (topic) => { setActiveTopic(topic); setView('topic'); };
  const handleSelectQuestion = (qa) => { setActiveQuestion(qa); setView('answer'); };
  const handleBackToTopics = () => { setView('home'); setActiveTopic(null); setActiveQuestion(null); };
  const handleBackToTopic = () => { setView('topic'); setActiveQuestion(null); };

  // breadcrumb label
  const breadcrumb = view === 'home' ? 'Help Center'
    : view === 'topic' ? activeTopic?.label
    : activeQuestion?.q?.slice(0, 32) + '…';

  return (
    <>
      <style>{`
        @keyframes helpbot-pop-in {
          from { opacity: 0; transform: scale(0.88) translateY(14px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes helpbot-fab-pulse {
          0%,100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent-primary) 35%, transparent); }
          50%      { box-shadow: 0 0 0 10px color-mix(in srgb, var(--accent-primary) 0%, transparent); }
        }
        .helpbot-fab   { animation: helpbot-fab-pulse 2.6s ease-in-out infinite; }
        .helpbot-win   { animation: helpbot-pop-in 0.22s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .helpbot-scroll::-webkit-scrollbar { width: 4px; }
        .helpbot-scroll::-webkit-scrollbar-track { background: transparent; }
        .helpbot-scroll::-webkit-scrollbar-thumb { background: var(--border-color); border-radius: 4px; }
      `}</style>

      {/* FAB */}
      {!open && (
        <button className="helpbot-fab" onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
            width: 54, height: 54, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--accent-primary), color-mix(in srgb, var(--accent-primary) 65%, var(--accent-info)))',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', transition: 'transform 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          title="Help Center"
        >
          <HelpCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div className="helpbot-win" dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
            width: 390, maxWidth: 'calc(100vw - 32px)',
            borderRadius: 20, overflow: 'hidden',
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 28px 72px rgba(0,0,0,0.3)',
            display: 'flex', flexDirection: 'column',
            maxHeight: minimized ? 'auto' : 580,
          }}
        >
          {/* ── Header ── */}
          <div style={{
            padding: '12px 14px',
            background: 'linear-gradient(135deg, var(--accent-primary)15 0%, var(--accent-info)08 100%)',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          }}>
            {/* Avatar */}
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-info))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={15} color="#fff" />
            </div>

            {/* Title + breadcrumb */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>
                Dashboard Help
              </p>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 500, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {breadcrumb}
              </p>
            </div>

            {/* Lang picker */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setShowLangPicker(p => !p)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 7px', borderRadius: 7,
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-tertiary)',
                  cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600,
                }}>
                <Globe size={11} />
                {currentLang?.flag} {currentLang?.code.toUpperCase()}
              </button>
              {showLangPicker && (
                <div style={{
                  position: 'absolute', top: '110%', right: 0,
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 12, overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  zIndex: 10, minWidth: 150,
                }}>
                  {LANGUAGES.map(l => (
                    <button key={l.code} onClick={() => { setLang(l.code); setShowLangPicker(false); }}
                      style={{
                        width: '100%', padding: '7px 13px', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        backgroundColor: l.code === lang ? 'var(--accent-primary)15' : 'transparent',
                        color: l.code === lang ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        fontSize: 12, fontWeight: l.code === lang ? 700 : 500,
                        textAlign: 'left',
                      }}>
                      <span>{l.flag}</span><span>{l.label}</span>
                      {l.code === lang && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Home shortcut */}
            {view !== 'home' && (
              <button onClick={handleReset} title="Back to Home"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4, borderRadius: 6, lineHeight: 0 }}>
                <Home size={14} />
              </button>
            )}

            {/* Minimize */}
            <button onClick={() => setMinimized(p => !p)} title={minimized ? 'Expand' : 'Minimize'}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4, borderRadius: 6, lineHeight: 0 }}>
              {minimized ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
            </button>

            {/* Close */}
            <button onClick={handleClose} title="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4, borderRadius: 6, lineHeight: 0 }}>
              <X size={15} />
            </button>
          </div>

          {/* ── Body ── */}
          {!minimized && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {view === 'home' && (
                <HomeView
                  topics={t.topics}
                  greeting={t.greeting}
                  onSelectTopic={handleSelectTopic}
                  isRTL={isRTL}
                />
              )}
              {view === 'topic' && activeTopic && (
                <TopicView
                  topic={activeTopic}
                  onBack={handleBackToTopics}
                  onSelectQuestion={handleSelectQuestion}
                  isRTL={isRTL}
                />
              )}
              {view === 'answer' && activeQuestion && activeTopic && (
                <AnswerView
                  question={activeQuestion}
                  topic={activeTopic}
                  onBack={handleBackToTopic}
                  navigate={navigate}
                  onClose={handleClose}
                  isRTL={isRTL}
                />
              )}
            </div>
          )}

          {/* ── Footer ── */}
          {!minimized && (
            <div style={{
              borderTop: '1px solid var(--border-color)',
              padding: '8px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
              backgroundColor: 'var(--bg-primary)',
            }}>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--text-tertiary)' }}>
                {t.topics.reduce((acc, tp) => acc + tp.questions.length, 0)} help articles · {t.topics.length} topics
              </p>
              <button onClick={handleReset}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 600, padding: 0,
                }}>
                <RotateCcw size={11} /> Home
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}